import { api } from "../api/client";
import type { CognitiveGraphEdge, ExtractedFact, MemoryRecord } from "../api/types";
import { store } from "../state";
import { escapeHtml } from "./html";

/**
 * Painel de detalhe de um no.
 *
 * Relacoes vem do grafo; FATOS nao. Fato escalar (sujeito -> predicado -> valor) e propriedade do
 * no e nunca virou aresta (ADR-015), entao ele e lido do bloco cognitivo das memorias que
 * sustentam o no — o que tambem evita duplicar dado num read model sem consumidor que o exija.
 */
export class NodeDetailPanel {
  private loadToken = 0;

  constructor(
    private readonly root: HTMLElement,
    private readonly onFocus: (key: string) => void,
    private readonly onClose: () => void
  ) {
    store.subscribe((state) => void this.render(state.selectedNodeKey));
    void this.render(null);
  }

  private async render(selectedKey: string | null): Promise<void> {
    const token = ++this.loadToken;
    const graph = store.state.graph;
    if (!selectedKey || !graph) {
      this.root.classList.remove("open");
      this.root.innerHTML = "";
      return;
    }

    const node = graph.nodes.find((candidate) => candidate.key === selectedKey);
    if (!node) {
      this.root.classList.remove("open");
      return;
    }

    const edges = graph.edges.filter((edge) => edge.from === selectedKey || edge.to === selectedKey);
    // O envelope do grafo cresce por adicao (ADR-015), entao o cliente nao pode quebrar quando um
    // campo ainda nao vem: espalhar um `undefined` lanca, e a excecao acontece antes de o painel
    // abrir — o clique no no simplesmente nao faz nada, sem erro visivel.
    const memoryIds = [
      ...new Set([
        ...(node.sourceMemoryIds ?? []),
        ...edges.flatMap((edge) => edge.sourceMemoryIds ?? [])
      ])
    ];

    this.root.classList.add("open");
    this.root.innerHTML = `
      <header class="panel-head">
        <div>
          <p class="eyebrow">${node.registered ? "Entidade registrada" : "Nó derivado"}</p>
          <h2>${escapeHtml(node.displayName)}</h2>
          <p class="muted mono">${escapeHtml(node.key)}</p>
        </div>
        <button class="ghost-button" id="detail-close" aria-label="Fechar">×</button>
      </header>
      <div class="detail-chips">
        ${node.entityType ? `<span class="chip">${escapeHtml(node.entityType)}</span>` : ""}
        ${node.scope ? `<span class="chip">${escapeHtml(node.scope)}</span>` : ""}
        <span class="chip">${node.degree} relaç${node.degree === 1 ? "ão" : "ões"}</span>
        <span class="chip">${node.usageCount} uso${node.usageCount === 1 ? "" : "s"}</span>
      </div>
      ${
        node.registered
          ? ""
          : `<p class="detail-warning">Este nó aparece como ponta de uma relação aprovada, mas não está no registro canônico de entidades.</p>`
      }
      <button class="secondary-button full" id="detail-focus">Explorar a partir daqui</button>
      <section class="detail-section">
        <h3>Relações <span class="muted">${edges.length}</span></h3>
        ${this.renderEdges(selectedKey, edges)}
      </section>
      <section class="detail-section" id="detail-facts">
        <h3>Fatos</h3>
        <p class="muted">Carregando…</p>
      </section>
      <section class="detail-section" id="detail-evidence">
        <h3>Evidência <span class="muted">${memoryIds.length}</span></h3>
        <p class="muted">Carregando…</p>
      </section>
    `;

    this.root.querySelector("#detail-close")!.addEventListener("click", () => this.onClose());
    this.root.querySelector("#detail-focus")!.addEventListener("click", () => this.onFocus(selectedKey));

    // Publicar na store re-entra aqui, porque este painel tambem e assinante dela. Reaproveitar
    // a evidencia ja carregada para o mesmo no fecha o ciclo: a segunda passada nao busca nem
    // publica. Sem isso o painel entra em laco — medido em 162 mil requisicoes de memoria antes
    // de a guarda existir, todas contra a API real.
    const alreadyLoaded = store.state.evidenceFor === selectedKey;
    const memories = alreadyLoaded ? store.state.evidence : await this.loadMemories(memoryIds);
    if (token !== this.loadToken) {
      return;
    }

    this.renderFacts(selectedKey, node.displayName, memories);
    this.renderEvidence(memories, memoryIds.length);
    if (!alreadyLoaded) {
      // Uma carga, dois consumidores: o painel lista e o grafo desenha os satelites. A origem vai
      // junto para que evidencia de uma selecao anterior nunca seja pendurada no no atual.
      store.update({ evidence: memories, evidenceFor: selectedKey });
    }
  }

  private renderEdges(selectedKey: string, edges: CognitiveGraphEdge[]): string {
    if (edges.length === 0) {
      return `<p class="muted">Nenhuma relação aprovada toca este nó.</p>`;
    }

    const nodesByKey = new Map((store.state.graph?.nodes ?? []).map((node) => [node.key, node]));
    const label = (key: string) => escapeHtml(nodesByKey.get(key)?.displayName ?? key);

    return `<ul class="relation-list">
      ${edges
        .map((edge) => {
          const outgoing = edge.from === selectedKey;
          const other = outgoing ? edge.to : edge.from;
          return `
            <li>
              <span class="relation-direction">${outgoing ? "→" : "←"}</span>
              <span class="relation-predicate">${escapeHtml(edge.predicate)}</span>
              <button class="link-button" data-key="${escapeHtml(other)}">${label(other)}</button>
              <span class="relation-evidence" title="Memórias que sustentam esta relação">${edge.sourceMemoryIds.length}×</span>
            </li>`;
        })
        .join("")}
    </ul>`;
  }

  private renderFacts(selectedKey: string, displayName: string, memories: MemoryRecord[]): void {
    const container = this.root.querySelector("#detail-facts");
    if (!container) {
      return;
    }

    const facts = memories
      .flatMap((memory) => readCognitiveFacts(memory.metadataJson))
      .filter((fact) => matchesSubject(fact.subject, selectedKey, displayName));

    container.innerHTML = `
      <h3>Fatos <span class="muted">${facts.length}</span></h3>
      ${
        facts.length === 0
          ? `<p class="muted">Nenhum fato escalar registrado para este nó.</p>`
          : `<ul class="fact-list">${facts
              .map(
                (fact) =>
                  `<li><span class="fact-predicate">${escapeHtml(fact.predicate)}</span><span class="fact-value">${escapeHtml(fact.value)}</span></li>`
              )
              .join("")}</ul>`
      }`;
  }

  private renderEvidence(memories: MemoryRecord[], requested: number): void {
    const container = this.root.querySelector("#detail-evidence");
    if (!container) {
      return;
    }

    container.innerHTML = `
      <h3>Evidência <span class="muted">${requested}</span></h3>
      ${
        memories.length === 0
          ? `<p class="muted">Nenhuma memória pôde ser carregada.</p>`
          : `<ul class="evidence-list">${memories
              .map(
                (memory) => `
                  <li>
                    <strong>${escapeHtml(memory.title)}</strong>
                    <p>${escapeHtml(memory.summary || memory.content)}</p>
                    <span class="chip quiet">${escapeHtml(memory.scope)}</span>
                  </li>`
              )
              .join("")}</ul>`
      }`;
  }

  private async loadMemories(memoryIds: string[]): Promise<MemoryRecord[]> {
    const results = await Promise.allSettled(memoryIds.map((id) => api.getMemory(id)));
    return results
      .filter((result): result is PromiseFulfilledResult<MemoryRecord> => result.status === "fulfilled")
      .map((result) => result.value);
  }
}

function readCognitiveFacts(metadataJson: string | null): ExtractedFact[] {
  if (!metadataJson) {
    return [];
  }
  try {
    const parsed = JSON.parse(metadataJson) as { cognitive?: { facts?: ExtractedFact[] } };
    return parsed.cognitive?.facts ?? [];
  } catch {
    // Metadata legada ou malformada degrada em silencio, como no backend.
    return [];
  }
}

function matchesSubject(subject: string, key: string, displayName: string): boolean {
  const normalized = subject.trim().toLowerCase().replace(/\s+/g, "_");
  return normalized === key.toLowerCase() || subject.trim().toLowerCase() === displayName.toLowerCase();
}
